import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { AnalyticsSeries, AnalyticsSeriesCurrencyRow, AnalyticsSeriesMetric } from "@expertloop/shared";

interface ActivitySectionProps {
	series: AnalyticsSeries;
}

/** Une grandeur comptée en nombre ne se met pas en forme comme une somme d'argent. */
function isMonetary(metric: AnalyticsSeriesMetric): boolean {
	return metric !== "capturedOrders";
}

/**
 * Évolution d'une grandeur encaissée, une bande par pas de temps.
 *
 * Chaque devise a son propre graphe et sa propre échelle : superposer deux
 * devises sur un même axe reviendrait à les comparer, ce que rien n'autorise
 * sans taux de change.
 */
export function ActivitySection({ series }: ActivitySectionProps) {
	const { t } = useI18n();

	if (series.byCurrency.length === 0) {
		return <p className="admin-overview__state">{t("adminOverview.empty")}</p>;
	}

	return (
		<div className="admin-overview__charts">
			{series.byCurrency.map((row) => (
				<CurrencyChart key={row.currency} row={row} metric={series.metric} />
			))}
		</div>
	);
}

function CurrencyChart({ row, metric }: { row: AnalyticsSeriesCurrencyRow; metric: AnalyticsSeriesMetric }) {
	const { formatCents } = useMoneyFormatter();
	// L'échelle est propre à la devise affichée, jamais commune à plusieurs.
	const peak = Math.max(...row.points.map((point) => point.value), 0);
	const total = row.points.reduce((sum, point) => sum + point.value, 0);

	const format = (value: number) =>
		isMonetary(metric) ? formatCents(value, row.currency) : String(value);

	return (
		<figure className="admin-overview__chart">
			<figcaption className="admin-overview__chart-caption">
				<span className="admin-overview__chart-currency">{row.currency.toUpperCase()}</span>
				<strong>{format(total)}</strong>
			</figcaption>
			<div className="admin-overview__bars">
				{row.points.map((point) => (
					<span
						key={point.key}
						className="admin-overview__bar"
						// Un pas sans activité garde une hauteur nulle : il existe, il est vide.
						style={{ height: peak > 0 ? `${Math.round((point.value / peak) * 100)}%` : "0%" }}
						title={`${point.key} — ${format(point.value)}`}
					/>
				))}
			</div>
		</figure>
	);
}
